import { AttributeOption, Coverage, Driver } from "@deathstar/types/northstar";
import { DriverDto, formatLicense, getDriverCoverageAdditions, hasCompletedAdvancedDriverReport } from "@deathstar/types/waypoint";
import { Alert, BeneficiaryDialog, Checkbox, classNames, InputField, PrimaryButton, useSnackbar } from "@deathstar/ui";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { Button, CircularProgress } from "@material-ui/core";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import api from "../../api/api";
import { useAccountId } from "../../api/useAccountId";
import { queryClient } from "../../util/queryClient";
import { Dialog, DialogProps } from "../../components/dialog/Dialog";
import CoverageSelector from "../equipment/CoverageSelector";
import {
    getDriverChangeDetail,
    getPendingDriverChangeValues,
    usePendingPolicyChanges,
} from "../pending-policy-changes/PendingPolicyChanges";
import { getDirtyValues, useSubscribedDirtyFields } from "../pending-policy-changes/useSubscribedDirtyFields";
import { OwnerOperatorEmployerLinker } from "./OwnerOperatorEmployerLinker";

type ExistingDriver = Partial<Driver> & { id: number; experience?: number | null; isOwnerOperator?: boolean | null };

export function NewDriverDialog({
    open,
    onClose,
    dialogProps,
    defaultValues,
    editingId,
    coverageFilter,
    getExistingDriver,
    deferPolicyChanges,
    pendingChangeDetail,
}: {
    open: boolean;
    onClose(): void;
    dialogProps?: Partial<Omit<DialogProps, "open" | "onClose" | "children">>;
    defaultValues?: Partial<DriverDto>;
    editingId?: number;
    coverageFilter?: (coverage: Coverage) => boolean;
    deferPolicyChanges?: boolean;
    pendingChangeDetail?: string;
    /**
     * This is used to check if the License entered matches any existing driver. We cannot just query Blazar for this since
     * the context matters (i.e. if this is used on the Monthly Report, the coverages on the driver are different than the coverages
     * that would be returned from just querying the current active drivers list).
     *
     * If a match is found, it is assumed that the user wants to edit the existing driver, and the form is reset with the existing
     * driver's data.
     *
     * If no match is found, a query is made to Blazar to see if the license matches any non-active driver on the account. Then,
     * if a match is found, the form is reset with the existing driver's data.
     */
    getExistingDriver?: (vin: string) => ExistingDriver | undefined;
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            {...dialogProps}
            className={classNames("w-screen p-4 md:max-w-[1080px]", dialogProps?.className)}
        >
            <NewDriverForm
                open={open}
                onClose={onClose}
                defaultValues={defaultValues}
                editingId={editingId}
                coverageFilter={coverageFilter}
                getExistingDriver={getExistingDriver}
                deferPolicyChanges={deferPolicyChanges}
                pendingChangeDetail={pendingChangeDetail}
            />
        </Dialog>
    );
}

function NewDriverForm({
    open,
    onClose,
    defaultValues,
    editingId,
    coverageFilter,
    getExistingDriver,
    deferPolicyChanges,
    pendingChangeDetail,
}: {
    open: boolean;
    onClose(): void;
    defaultValues?: Partial<DriverDto>;
    editingId?: number;
    coverageFilter?: (coverage: Coverage) => boolean;
    getExistingDriver?: (vin: string) => ExistingDriver | undefined;
    deferPolicyChanges?: boolean;
    pendingChangeDetail?: string;
}) {
    const accountId = useAccountId();
    const pendingPolicyChanges = usePendingPolicyChanges();
    const [isSaving, setIsSaving] = useState(false);

    const create = api.drivers.useCreate(accountId!, {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!) });
            setIsSaving(true);
            onClose();
        },
        onError: (error) => {
            console.error(error);
            if (error.status === 403) {
                useSnackbar.add("You do not have permission to manage drivers.", { variant: "error" });
            } else if (error?.message) {
                useSnackbar.add(error?.message, { variant: "error" });
            } else {
                useSnackbar.add("Could not add driver. Please try again later.", { variant: "error" });
            }
        },
    });
    const update = api.drivers.useUpdate(accountId!, {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!) });
            setIsSaving(true);
            onClose();
        },
        onError: (error) => {
            console.error(error);
            if (error.status === 403) {
                useSnackbar.add("You do not have permission to manage drivers.", { variant: "error" });
            } else if (error?.message) {
                useSnackbar.add(error?.message, { variant: "error" });
            } else {
                useSnackbar.add("Could not update driver. Please try again later.", { variant: "error" });
            }
        },
    });

    const form = useForm<DriverDto>({
        defaultValues: {
            coverages: [],
            ...defaultValues,
            ...(editingId ? getPendingDriverChangeValues(pendingPolicyChanges.changes, accountId!, editingId) : {}),
        },
    });
    const [originalCoverages, setOriginalCoverages] = useState(defaultValues?.coverages || []);
    const subscribedDirtyFields = useSubscribedDirtyFields(form);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setOriginalCoverages(defaultValues?.coverages || []);
            form.reset({
                coverages: [],
                ...defaultValues,
                ...(editingId ? getPendingDriverChangeValues(pendingPolicyChanges.changes, accountId!, editingId) : {}),
            });
        }
        wasOpen.current = open;
    }, [accountId, defaultValues, editingId, form, open, pendingPolicyChanges.changes]);

    const license = form.watch("license");
    const licenseField = form.register("license", { required: "Please enter a license" });
    const [existingDriverId, setExistingDriverId] = useState<number | null>(null);
    const [driverSpecified, setDriverSpecified] = useState(false);
    const [employerLinkerOpen, setEmployerLinkerOpen] = useState(false);
    const [beneficiaryDialogOpen, setBeneficiaryDialogOpen] = useState(false);
    const isEditing = !!editingId || !!existingDriverId;
    const isOwnerOperator = form.watch("ownerOperator");

    const { data: employers } = api.ownerOperatorEmployers.useGetForAccount(accountId!);
    const existingDriverValues = useCallback(
        (driver: ExistingDriver, coverages = driver.coverages ?? []): Partial<DriverDto> => ({
            ...defaultValues,
            coverages,
            name: driver.name ?? undefined,
            state: driver.state ?? undefined,
            license: driver.license ?? undefined,
            dob: driver.dob ?? undefined,
            doh: driver.policyDoh ?? driver.doh ?? undefined,
            exp: driver.policyExp ?? driver.experience ?? driver.exp ?? undefined,
            ownerOperator: driver.policyOwnerOperator ?? driver.isOwnerOperator ?? driver.ownerOperator ?? undefined,
            employer: driver.employerId ?? undefined,
            beneficiaries: driver.beneficiaries ?? undefined,
        }),
        [defaultValues]
    );

    const checkExistingDriver = useMemo(
        () =>
            debounce((license: string) => {
                const driver = getExistingDriver!(license); // getExistingDriver is required if editingId is not provided, this will crash otherwise
                if (driver) {
                    setExistingDriverId(driver.id);
                    setOriginalCoverages(driver.coverages || []);
                    if (!isSaving) {
                        useSnackbar.add("Previous driver record loaded. Choose coverage and click 'Update' to continue", {
                            autoHideDuration: 10000,
                            variant: "info",
                        });
                    }
                    form.reset(existingDriverValues(driver));
                } else {
                    api.drivers.findByLicense(accountId!, license).then((driver) => {
                        if (driver && !isSaving) {
                            useSnackbar.add("Previous driver record loaded. Choose coverage and click 'Update' to continue", {
                                autoHideDuration: 10000,
                                variant: "info",
                            });
                            setExistingDriverId(driver.id!);
                            setOriginalCoverages([]);
                            form.reset(existingDriverValues(driver as ExistingDriver, []));
                        }
                    });
                }
            }, 300),
        [accountId, existingDriverValues, form, getExistingDriver, isSaving]
    );

    useEffect(() => {
        if (!editingId && license && license.length > 4 && !existingDriverId) {
            checkExistingDriver(license);
        }
    }, [accountId, checkExistingDriver, editingId, existingDriverId, form, license]);

    const employer = form.watch("employer");
    const selectedCoverages = form.watch("coverages") || [];
    const atDate = form.watch("atDate");
    const policiesQuery = api.policies.useFind(accountId!, "active");
    const reportsQuery = api.monthlyReports.useFind(accountId!, { enabled: open && !!accountId, staleTime: 0 });
    const additions = getDriverCoverageAdditions(selectedCoverages, isEditing ? originalCoverages : []);
    const completedPoliciesForDate = (date: string) =>
        (policiesQuery.data || []).filter((policy) => {
            if (!additions.some((coverage) => coverage.policyId === policy.id)) return false;
            const reports = reportsQuery.data?.find((reportPolicy) => reportPolicy.policyId === policy.id)?.reports || [];
            return hasCompletedAdvancedDriverReport(policy, reports, date);
        });
    // The UI only manages a single beneficiary for now, even though the backend supports multiple
    const beneficiary = form.watch("beneficiaries")?.[0] ?? null;
    const showBeneficiary =
        !!isOwnerOperator &&
        (policiesQuery.data || []).some(
            (policy) =>
                policy.attributes.some((attribute) => attribute.attributeOptionId === AttributeOption.Id.BENEFICIARY) &&
                selectedCoverages.some((coverage) => !coverage.remove && coverage.policyId === policy.id)
        );
    const today = moment().format("YYYY-MM-DD");
    const nextMonth = moment().add(1, "month").startOf("month").format("YYYY-MM-DD");
    const defaultToNextMonth = completedPoliciesForDate(today).length > 0;
    const reviewPolicies = completedPoliciesForDate(atDate);
    const checkingReports =
        additions.length > 0 && (policiesQuery.isPending || reportsQuery.isPending || policiesQuery.isError || reportsQuery.isError);

    useEffect(() => {
        if (!open || form.formState.touchedFields.atDate || checkingReports) return;
        const defaultDate = defaultToNextMonth ? nextMonth : defaultValues?.atDate || today;
        if (form.getValues("atDate") !== defaultDate) {
            form.setValue("atDate", defaultDate, { shouldValidate: true });
        }
    }, [open, form, form.formState.touchedFields.atDate, checkingReports, defaultToNextMonth, nextMonth, today, defaultValues?.atDate]);

    return (
        <>
            <OwnerOperatorEmployerLinker
                open={employerLinkerOpen}
                currentValue={(() => {
                    const employer = form.watch("employer");
                    return typeof employer === "number" ? employer : null;
                })()}
                onClose={() => setEmployerLinkerOpen(false)}
                onChange={(employer) => {
                    form.setValue("employer", employer, { shouldDirty: true, shouldTouch: true });
                    setEmployerLinkerOpen(false);
                }}
                driver={{ name: form.watch("name") || "" }}
            />
            <BeneficiaryDialog
                open={beneficiaryDialogOpen}
                currentValue={beneficiary}
                onClose={() => setBeneficiaryDialogOpen(false)}
                onChange={(value) => {
                    form.setValue("beneficiaries", value ? [value] : [], { shouldDirty: true, shouldTouch: true });
                }}
            />
            <form
                onSubmit={form.handleSubmit((data) => {
                    const label = `${isEditing && !(deferPolicyChanges && existingDriverId && !editingId) ? "Update" : "Add"} driver`;
                    if (isEditing) {
                        if (!update.isPending) {
                            const dirtyFields = getDirtyValues(data, subscribedDirtyFields);
                            const change = {
                                id: editingId || existingDriverId!,
                                atDate: data.atDate,
                                ...(existingDriverId && !editingId ? data : dirtyFields),
                            };
                            if (deferPolicyChanges) {
                                const { id, ...changes } = change;
                                pendingPolicyChanges.addChange({
                                    accountId: accountId!,
                                    kind: "drivers",
                                    label,
                                    detail: pendingChangeDetail || getDriverChangeDetail(data.name),
                                    submit: () => api.drivers.update(accountId!, id, changes),
                                    unitUpdate: { kind: "drivers", id, changes, displayValues: data, originalValues: defaultValues },
                                });
                                queryClient.invalidateQueries({ queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!) });
                                setIsSaving(true);
                                onClose();
                            } else {
                                update.mutate(change);
                            }
                        }
                    } else {
                        if (!create.isPending) {
                            if (deferPolicyChanges) {
                                pendingPolicyChanges.addChange({
                                    accountId: accountId!,
                                    kind: "drivers",
                                    label,
                                    detail: getDriverChangeDetail(data.name),
                                    submit: () => api.drivers.create(accountId!, data),
                                    unitUpdate: { kind: "drivers", isCreate: true, changes: data, displayValues: data },
                                });
                                queryClient.invalidateQueries({ queryKey: api.ownerOperatorEmployers.queryKeys.get(accountId!) });
                                setIsSaving(true);
                                onClose();
                            } else {
                                create.mutate(data);
                            }
                        }
                    }
                })}
                className="w-full space-y-4 text-sm"
            >
                {!driverSpecified && !editingId ? (
                    <div>
                        <p className="my-4 flex items-center justify-center text-2xl font-medium text-waypoint-blue-dark">
                            What type of driver is this?
                        </p>
                        <div className="my-8 flex flex-col gap-20 md:flex-row md:justify-center">
                            <Button
                                className="w-90 h-20 text-2xl"
                                size="large"
                                variant="outlined"
                                onClick={() => {
                                    form.setValue("ownerOperator", true);
                                    setDriverSpecified(true);
                                }}
                                style={{ textTransform: "none" }}
                            >
                                Indepedent Contractor
                            </Button>
                            <Button
                                className="h-90 w-80 text-2xl"
                                size="large"
                                variant="outlined"
                                onClick={() => {
                                    form.setValue("ownerOperator", false);
                                    setDriverSpecified(true);
                                }}
                                style={{ textTransform: "none" }}
                            >
                                Company Driver
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <FormProvider {...form}>
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                                <div className="col-span-2">
                                    <div>
                                        <p className="text-lg font-medium text-waypoint-blue-dark">Details</p>
                                        <div className="grid grid-cols-12 gap-x-4 gap-y-2">
                                            <InputField
                                                className="col-span-8 md:col-span-4"
                                                classes={{ inputContainer: "py-2", input: classNames(isEditing && "opacity-50") }}
                                                required
                                                label="License #"
                                                {...licenseField}
                                                onChange={(e) => {
                                                    e.target.value = formatLicense(e.target.value);
                                                    return licenseField.onChange(e);
                                                }}
                                                // `readOnly`, not `disabled`: react-hook-form re-reads a disabled input's DOM
                                                // value as undefined on every render, silently dropping the loaded license
                                                readOnly={isEditing}
                                                placeholder="A123456789"
                                            />
                                            <InputField
                                                className="col-span-8 md:col-span-5"
                                                classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                                required
                                                label="Name"
                                                {...form.register("name", { required: "Please enter a name" })}
                                                placeholder="John Doe"
                                            />
                                            <InputField
                                                className="col-span-4 md:col-span-4"
                                                classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                                required
                                                label="State"
                                                {...form.register("state", {
                                                    required: "Please enter a state",
                                                    validate: (value) =>
                                                        (value && value.length === 2) || "Please enter a 2-letter state code",
                                                })}
                                                placeholder="MI"
                                            />
                                            <InputField
                                                className="col-span-6 md:col-span-4"
                                                classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                                required
                                                label="Date of birth"
                                                {...form.register("dob", { required: "Please enter a date of birth" })}
                                                type="date"
                                            />
                                            <InputField
                                                className="col-span-6 md:col-span-4"
                                                classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                                required
                                                label="Date hired"
                                                {...form.register("doh")}
                                                type="date"
                                            />
                                            <InputField
                                                className="col-span-6 md:col-span-4"
                                                classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                                label="Years experience"
                                                required
                                                {...form.register("exp", { setValueAs: (value) => (value ? parseInt(value) : null) })}
                                                type="number"
                                                min={0}
                                            />

                                            <div className="col-span-full space-y-2">
                                                <Checkbox
                                                    classes={{ input: "w-4 h-4", root: "text-sm" }}
                                                    label="Independent contractor"
                                                    {...form.register("ownerOperator")}
                                                />
                                                {!isEditing && (
                                                    <Checkbox
                                                        classes={{ input: "w-4 h-4" }}
                                                        required
                                                        label={
                                                            <span>
                                                                I confirm this driver meets my insurance company's driver guidelines
                                                                <span className="text-sm text-red-600"> *</span>
                                                            </span>
                                                        }
                                                        {...form.register("wpUserConfirmationMeetsGuidelines", { required: true })}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-1">
                                    <CoverageSelector dataType={"drivers"} isEditing={isEditing} coverageFilter={coverageFilter} />
                                </div>
                            </div>
                        </FormProvider>

                        <div className="flex items-end justify-between pt-4">
                            <div className="flex flex-row gap-4 space-y-2">
                                <InputField
                                    type="date"
                                    label={isEditing ? "Make changes effective on" : "Add to coverage on"}
                                    classes={{ inputContainer: "py-2", input: "disabled:opacity-50" }}
                                    {...form.register("atDate", {
                                        required: "Please select a date",
                                        validate: (value) => {
                                            const m = moment(value, "YYYY-MM-DD", true);
                                            if (!m.isValid()) {
                                                return "Invalid date";
                                            }
                                            if (defaultToNextMonth && value === nextMonth) return true;
                                            // An earlier completed month is allowed as an AM review request, including after the 15th.
                                            if (reviewPolicies.length && m.isSame(moment().subtract(1, "month"), "month")) return true;
                                            if (m.isAfter(moment(), "day")) {
                                                return "Date cannot be in the future";
                                            }

                                            if (moment().date() >= 15 && m.isBefore(moment(), "month")) {
                                                return "Date is too far in the past";
                                            }

                                            if (moment().date() < 15 && m.isBefore(moment().subtract(1, "month"), "month")) {
                                                return "Date is too far in the past";
                                            }

                                            return true;
                                        },
                                    })}
                                />
                                {isOwnerOperator && (
                                    <div className="flex flex-col">
                                        <label className="-mt-2">
                                            Independent Contractor Address <span className="text-red-600">*</span>
                                        </label>
                                        <button
                                            type="button"
                                            className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap rounded border border-stone-300 px-3 py-2 text-center text-sm ring-blue-700 hover:bg-stone-50 focus:bg-stone-100 focus:ring-1"
                                            onClick={() => setEmployerLinkerOpen(true)}
                                        >
                                            {employer ? (
                                                typeof employer === "object" ? (
                                                    <span>{employer.name}</span>
                                                ) : (
                                                    <span>{employers?.find((e) => e.id === employer)?.name}</span>
                                                )
                                            ) : (
                                                <span className="italic text-stone-400">IC Address - Unassigned</span>
                                            )}
                                        </button>
                                        <input
                                            type="hidden"
                                            {...form.register("employer", {
                                                validate: (value) => {
                                                    if (isOwnerOperator && !value) {
                                                        return "Please select an address for independent contractors";
                                                    }
                                                    return true;
                                                },
                                            })}
                                        />
                                    </div>
                                )}
                                {showBeneficiary && (
                                    <div className="flex flex-col">
                                        <label className="-mt-2">Beneficiary Address</label>
                                        <button
                                            type="button"
                                            className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap rounded border border-stone-300 px-3 py-2 text-center text-sm ring-blue-700 hover:bg-stone-50 focus:bg-stone-100 focus:ring-1"
                                            onClick={() => setBeneficiaryDialogOpen(true)}
                                        >
                                            {beneficiary ? (
                                                <span>{beneficiary.name}</span>
                                            ) : (
                                                <span className="italic text-stone-400">Beneficiary - Unassigned</span>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {isEditing ? (
                                <PrimaryButton
                                    type="submit"
                                    disabled={update.isPending || checkingReports || !form.formState.isDirty}
                                    className="w-32"
                                >
                                    {update.isPending ? (
                                        <>
                                            <span>Loading</span>
                                            <CircularProgress size="1rem" classes={{ circle: "text-white" }} />
                                        </>
                                    ) : (
                                        <>
                                            <span>Update</span>
                                            <ArrowRightCircleIcon className="h-4 w-4" />
                                        </>
                                    )}
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    disabled={create.isPending || checkingReports || !form.watch("wpUserConfirmationMeetsGuidelines")}
                                    className="w-32"
                                >
                                    {create.isPending ? (
                                        <>
                                            <span>Loading</span>
                                            <CircularProgress size="1rem" classes={{ circle: "text-white" }} />
                                        </>
                                    ) : (
                                        <>
                                            <span>Create</span>
                                            <ArrowRightCircleIcon className="h-4 w-4" />
                                        </>
                                    )}
                                </PrimaryButton>
                            )}
                        </div>
                        {reviewPolicies.length > 0 && (
                            <Alert variant="info" role="status">
                                The {moment(atDate).format("MMMM YYYY")} report for{" "}
                                {reviewPolicies.map((policy) => policy.number).join(", ")} has already been submitted or finalized. Adding
                                this driver on your selected date could change that report, so your request will be sent to your account
                                manager for review before it is applied to these policies.
                            </Alert>
                        )}
                        {(policiesQuery.isError || reportsQuery.isError) && additions.length > 0 && (
                            <Alert variant="error">We couldn’t check the monthly reports. Please reopen this dialog and try again.</Alert>
                        )}
                        {Object.values(form.formState.errors).length > 0 && (
                            <p className="text-red-600">
                                {Object.values(form.formState.errors)
                                    .map((error) => error.message)
                                    .join("; ")}
                            </p>
                        )}
                    </>
                )}
            </form>
        </>
    );
}
